/* eslint-disable @typescript-eslint/no-explicit-any */
/* eslint-disable @typescript-eslint/no-non-null-assertion */
/* eslint-disable @nx/enforce-module-boundaries */
import { NgClass } from '@angular/common';
import {
  Component,
  inject,
  input,
  OnDestroy,
  OnInit,
  signal,
} from '@angular/core';
import { toObservable } from '@angular/core/rxjs-interop';
import { ButtonModule } from 'primeng/button';
import { DashboardTranslateKeys } from '../../../utils/dashboard-translate-keys.enum';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { FiltersComponent } from '../../filters/filters.component';
import { SelectButtonModule } from 'primeng/selectbutton';
import { FormsModule } from '@angular/forms';

import { SkeletonDashCardComponent } from '../../skeleton/skeleton.component';
import { ViewportObserverDirective } from '../../viewport-observer/viewport-observer.directive';
import { User } from '@job-v2/ats-utils';
import { DashboardService } from '../../../services/dashboard.service';
import { ColumnChart, StatisticsCriteria } from '../../../utils/dashboard.data';
import { Subject, takeUntil } from 'rxjs';
import { AppGlobalService } from 'apps/job-ats/src/app/commons/services/app-global.service';
import { MessageService } from 'primeng/api';
import { ColumnChartComponent } from '../../../charts/column-chart/column-chart.component';

@Component({
  imports: [
    ButtonModule,
    NgClass,
    TranslateModule,
    FiltersComponent,
    SelectButtonModule,
    FormsModule,
    ViewportObserverDirective,
    SkeletonDashCardComponent,
    ColumnChartComponent,
  ],
  selector: 'app-process-efficiency',
  templateUrl: './process-efficiency.component.html',
  styleUrl: './process-efficiency.component.scss',
})
export class ProcessEfficiencyComponent implements OnInit, OnDestroy {
  readonly selectedJobOwners = input<string[]>([]);
  private readonly selectedJobOwners$ = toObservable(this.selectedJobOwners);
  public isCollapsed = signal(false);
  public isInitialized = signal(false);
  public isLoadingRecruitmentDelay = signal(true);
  public isLoadingPublicationClosing = signal(true);
  private readonly translate = inject(TranslateService);
  private readonly service = inject(DashboardService);
  private readonly message = inject(MessageService);
  private readonly appGlobalService = inject(AppGlobalService);
  private readonly destroy$ = new Subject<void>();
  private recruitmentDelayCriteria = new StatisticsCriteria();
  private publicationClosingCriteria = new StatisticsCriteria();
  public user!: User;
  public DashboardTranslateKeys = DashboardTranslateKeys;
  public averageHiringObjects!: ColumnChart[];
  public averagePublicationClosing!: ColumnChart[];

  ngOnInit(): void {
    this.selectedJobOwners$
      .pipe(takeUntil(this.destroy$))
      .subscribe((selectedJobOwners) => {
        this.handleSelectedJobOwnersChange(selectedJobOwners);
      });
    this.appGlobalService.currentUser
      .pipe(takeUntil(this.destroy$))
      .subscribe((user) => {
        if (user) this.user = user;
        this.recruitmentDelayCriteria.companyId = user?.company?._id;
        this.publicationClosingCriteria.companyId = user?.company?._id;
        this.handleSelectedJobOwnersChange(this.selectedJobOwners());
      });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  toggleContent() {
    this.isCollapsed.set(!this.isCollapsed());
  }

  initData() {
    if (this.isInitialized()) return;
    this.isInitialized.set(true);
    this.loadAverageRecruitmentDelay();
    this.loadAveragePublicationClosingDelay();
  }

  loadAverageRecruitmentDelay() {
    if (!this.user.company?._id) return;
    this.isLoadingRecruitmentDelay.set(true);
    this.service
      .getAverageRecruitmentDelay(this.recruitmentDelayCriteria)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (res) => {
          this.averageHiringObjects = res.map((elt) => {
            return {
              structure: elt.structure,
              value: Math.floor(elt.avgDelay || 0),
            };
          });
          this.isLoadingRecruitmentDelay.set(false);
        },
        error: () => {
          this.displayErrorMessage();
          this.isLoadingRecruitmentDelay.set(false);
        },
      });
  }

  loadAveragePublicationClosingDelay() {
    if (!this.user.company?._id) return;
    this.isLoadingPublicationClosing.set(true);
    this.service
      .getAveragePublicationToClosingDelay(this.publicationClosingCriteria)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (res) => {
          this.averagePublicationClosing = res.map((elt) => {
            return {
              structure: elt.structure,
              value: Math.floor(elt.avgDelay || 0),
            };
          });
          this.isLoadingPublicationClosing.set(false);
        },
        error: () => {
          this.displayErrorMessage();
          this.isLoadingPublicationClosing.set(false);
        },
      });
  }

  private updateCriteria<T extends { monthYear?: string; year?: string }>(
    criteria: T,
    key: keyof T,
    value: string | null,
  ) {
    if (value) {
      criteria[key] = value as T[keyof T];
    } else {
      delete criteria[key];
    }
  }

  updateRecruitmentDelayYear($event: string | null) {
    this.updateCriteria(this.recruitmentDelayCriteria, 'year', $event);
    this.loadAverageRecruitmentDelay();
  }

  updatePublicationClosingYear($event: string | null) {
    this.updateCriteria(this.publicationClosingCriteria, 'year', $event);
    this.loadAveragePublicationClosingDelay();
  }

  private displayErrorMessage() {
    const summary = this.translate.instant(DashboardTranslateKeys.ERROR);
    const detail = this.translate.instant(
      DashboardTranslateKeys.ERROR_TRY_AGAIN,
    );
    this.message.add({ severity: 'error', summary, detail });
  }

  private updateSelectedJobOwners(selectedJobOwners: string[]) {
    this.recruitmentDelayCriteria.selectedJobOwner = selectedJobOwners;
    this.publicationClosingCriteria.selectedJobOwner = selectedJobOwners;
  }

  private handleSelectedJobOwnersChange(selectedJobOwners: string[]) {
    if (!this.user?.company?._id) return;
    this.updateSelectedJobOwners(selectedJobOwners);
    if (this.isInitialized()) {
      this.loadAverageRecruitmentDelay();
      this.loadAveragePublicationClosingDelay();
    }
  }
}
